import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { Globe2, Loader2 } from "lucide-react";

/** Per-country pricing panel — deterministic FX+VAT layer on top of Phase 2's
 *  GPT-5.2 CHF recommendation. Shown inside /dashboard/module/pricing. */
export function CountryPricingPanel() {
  const { lang } = useLang();
  const [products, setProducts] = useState([]);
  const [pricings, setPricings] = useState({});
  const [country, setCountry] = useState({});
  const [busy, setBusy] = useState({});

  const load = async () => {
    const [p, pr] = await Promise.all([
      api.get("/shopify/products?limit=100"),
      api.get("/intelligence/pricing"),
    ]);
    setProducts(p.data.products || []);
    setPricings(Object.fromEntries((pr.data.pricing || []).map((d) => [d.shopify_id, d])));
    // Pre-load any country results already computed.
    const results = {};
    for (const row of pr.data.pricing || []) {
      try {
        const { data } = await api.get(`/phase4/pricing/country/${row.shopify_id}`);
        results[row.shopify_id] = data;
      } catch { /* not yet computed */ }
    }
    setCountry(results);
  };

  useEffect(() => { load(); }, []);

  const compute = async (sid) => {
    setBusy((b) => ({ ...b, [sid]: true }));
    try {
      const { data } = await api.post(`/phase4/pricing/country/${sid}`);
      setCountry((c) => ({ ...c, [sid]: data }));
      toast.success(t(lang, "p4_pricing_done"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy((b) => ({ ...b, [sid]: false }));
    }
  };

  return (
    <div className="hq-card overflow-hidden" data-testid="country-pricing-panel">
      <div className="p-5 border-b border-white/10 flex items-center gap-2 flex-wrap">
        <Globe2 size={14} className="text-[color:var(--hq-cyan)]" />
        <div className="font-display text-lg">{t(lang, "p4_pricing_country_title")}</div>
        <span className="ml-auto font-mono text-[10px] uppercase text-white/40">
          VAT · FX · elasticity
        </span>
      </div>

      <div className="divide-y divide-white/5">
        {products.map((p) => {
          const pr = pricings[p.shopify_id];
          const cp = country[p.shopify_id];
          if (!pr) return null; // only show products with a base CHF recommendation
          return (
            <div key={p.shopify_id} className="p-5" data-testid={`p4-pricing-row-${p.shopify_id}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="text-sm flex-1 truncate">{p.title}</div>
                <div className="font-mono text-xs text-[color:var(--hq-cyan)]">
                  base: CHF {pr.recommended_price_chf}
                </div>
                <button
                  data-testid={`p4-pricing-compute-${p.shopify_id}`}
                  onClick={() => compute(p.shopify_id)}
                  disabled={!!busy[p.shopify_id]}
                  className="hq-btn hq-btn-ghost text-xs disabled:opacity-50"
                >
                  {busy[p.shopify_id] ? <Loader2 size={10} className="animate-spin" /> : null}
                  {cp ? t(lang, "p4_pricing_recompute") : t(lang, "p4_pricing_compute")}
                </button>
              </div>
              {cp && (
                <div className="grid gap-2 md:grid-cols-5">
                  {cp.countries.map((c) => (
                    <div key={c.country} className="border border-white/10 p-2">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="font-mono text-[10px] uppercase text-[color:var(--hq-cyan)]">{c.country}</div>
                        <div className="ml-auto font-mono text-[10px] text-white/40">
                          VAT {Math.round(c.vat_rate * 1000) / 10}%
                        </div>
                      </div>
                      <div className="text-base font-mono">{c.currency} {c.recommended_gross}</div>
                      <div className="text-[10px] text-white/50">net {c.recommended_net}</div>
                      <div className="text-[10px] font-mono text-white/30 mt-1">
                        ×{c.elasticity_multiplier} · fx {c.fx_rate_to_chf}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {products.length === 0 && (
          <div className="text-center py-8 font-mono text-xs text-white/40">
            {t(lang, "intel_no_products")}
          </div>
        )}
      </div>
    </div>
  );
}
